const DEFAULT_HONEYCOMB_OTLP_ENDPOINT = 'https://api.honeycomb.io/v1/traces';
const DEFAULT_SERVICE_NAME = 'findmeinzuhause-webapp';
const DEFAULT_SAMPLE_RATE = 1;

declare global {
  interface Window {
    __FMZ_OTEL_INITIALIZED__?: boolean;
  }
}

function readNonEmptyValue(value: string | undefined): string | undefined {
  if (!value) {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function parseSampleRate(rawValue: string | undefined): number {
  if (!rawValue) {
    return DEFAULT_SAMPLE_RATE;
  }

  const parsed = Number(rawValue);
  if (!Number.isFinite(parsed)) {
    return DEFAULT_SAMPLE_RATE;
  }

  return Math.min(1, Math.max(0, parsed));
}

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function buildTraceHeaderTargets(): RegExp[] {
  const targets = [new RegExp(`^${escapeRegex(window.location.origin)}`)];

  const supabaseUrl = readNonEmptyValue(import.meta.env.VITE_SUPABASE_URL);
  if (supabaseUrl) {
    targets.push(new RegExp(`^${escapeRegex(supabaseUrl)}`));
  }

  return targets;
}

async function importEsmShModule(url: string): Promise<any> {
  return import(/* @vite-ignore */ url);
}

async function importOptionalEsmShModule(url: string, label: string): Promise<any | null> {
  try {
    return await importEsmShModule(url);
  } catch (error) {
    console.warn(`Skipping OTEL ${label} instrumentation`, error);
    return null;
  }
}

async function initializeHoneycombOtel(): Promise<void> {
  if (window.__FMZ_OTEL_INITIALIZED__) {
    return;
  }

  const apiKey = readNonEmptyValue(import.meta.env.VITE_HONEYCOMB_API_KEY);
  if (!apiKey) {
    return;
  }

  const dataset = readNonEmptyValue(import.meta.env.VITE_HONEYCOMB_DATASET);
  const endpoint =
    readNonEmptyValue(import.meta.env.VITE_HONEYCOMB_OTLP_ENDPOINT) ?? DEFAULT_HONEYCOMB_OTLP_ENDPOINT;
  const sampleRate = parseSampleRate(import.meta.env.VITE_HONEYCOMB_SAMPLE_RATE);
  const serviceName = readNonEmptyValue(import.meta.env.VITE_OTEL_SERVICE_NAME) ?? DEFAULT_SERVICE_NAME;
  const environment =
    readNonEmptyValue(import.meta.env.VITE_OTEL_ENVIRONMENT) ?? import.meta.env.MODE;

  const [
    { OTLPTraceExporter },
    { Resource },
    { BatchSpanProcessor, ParentBasedSampler, TraceIdRatioBasedSampler },
    { WebTracerProvider },
  ] = await Promise.all([
    importEsmShModule('https://esm.sh/@opentelemetry/exporter-trace-otlp-http@0.57.1?target=es2020'),
    importEsmShModule('https://esm.sh/@opentelemetry/resources@1.30.1?target=es2020'),
    importEsmShModule('https://esm.sh/@opentelemetry/sdk-trace-base@1.30.1?target=es2020'),
    importEsmShModule('https://esm.sh/@opentelemetry/sdk-trace-web@1.30.1?target=es2020'),
  ]);

  const headers: Record<string, string> = {
    'x-honeycomb-team': apiKey,
  };

  if (dataset) {
    headers['x-honeycomb-dataset'] = dataset;
  }

  const provider = new WebTracerProvider({
    resource: new Resource({
      'service.name': serviceName,
      'deployment.environment': environment,
    }),
    sampler: new ParentBasedSampler({
      root: new TraceIdRatioBasedSampler(sampleRate),
    }),
  });

  provider.addSpanProcessor(
    new BatchSpanProcessor(
      new OTLPTraceExporter({
        url: endpoint,
        headers,
      }),
    ),
  );

  provider.register();

  // Basic app load span without external document-load package.
  const loadSpan = provider.getTracer(serviceName).startSpan('webapp.load');
  if (document.readyState === 'complete') {
    loadSpan.end();
  } else {
    window.addEventListener('load', () => loadSpan.end(), { once: true });
  }

  const [instrumentationModule, fetchModule, userInteractionModule] = await Promise.all([
    importOptionalEsmShModule('https://esm.sh/@opentelemetry/instrumentation@0.57.1?target=es2020', 'core'),
    importOptionalEsmShModule('https://esm.sh/@opentelemetry/instrumentation-fetch@0.57.1?target=es2020', 'fetch'),
    importOptionalEsmShModule(
      'https://esm.sh/@opentelemetry/instrumentation-user-interaction@0.57.1?target=es2020',
      'user-interaction',
    ),
  ]);

  const registerInstrumentations = instrumentationModule?.registerInstrumentations;
  if (typeof registerInstrumentations === 'function') {
    const instrumentations: unknown[] = [];

    if (typeof fetchModule?.FetchInstrumentation === 'function') {
      instrumentations.push(
        new fetchModule.FetchInstrumentation({
          propagateTraceHeaderCorsUrls: buildTraceHeaderTargets(),
        }),
      );
    }

    if (typeof userInteractionModule?.UserInteractionInstrumentation === 'function') {
      instrumentations.push(new userInteractionModule.UserInteractionInstrumentation());
    }

    if (instrumentations.length > 0) {
      registerInstrumentations({ instrumentations });
    }
  }

  window.__FMZ_OTEL_INITIALIZED__ = true;
}

void initializeHoneycombOtel().catch((error: unknown) => {
  console.warn('Honeycomb OTEL initialization failed', error);
});
